import { useCallback, useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { useAuth0 } from '@auth0/auth0-react';
import * as state from '@/components/Analysis/state';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Box, MenuItem, TextField } from '@mui/material';
import { CircularProgress } from '@/components/shared/CircularProgress';
import { abyssColors } from '@/theme/colors';
import { calculatePercentage, getPipeSize, getTemplateFieldData, titleCaseSentence } from '@/utils';
import { localiseMeasurement } from '@/utils/unitSystem';
import { useDefectLegendColors } from '@/components/Analysis/shared/hooks/useDefectLegendColors';
import { coatingConditionLegend } from '@/constants';
import {
  EquipmentDetailTypeEnum,
  OptionFieldTypeEnum,
  SortTypeEnum,
  UnitsEnum,
} from '@/__generated__/graphql';
import { TableCellStyleEnum } from '@/__generated__/graphql';
import { EditableReportField } from '../EditableReportField';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { EditableFieldRef } from '../EditableReportField/EditableReportField';
import { LazyDatePickerCell } from '@/components/Analysis/shared/DatePickerCell';

interface InlineProps {
  fieldKey: string;
  fieldValue: string | number;
  fieldName: string;
}

export const useFieldFormatting = (
  assemblyDetails: Record<string, string> | undefined,
  assemblyArea: number,
  assemblyId?: string
) => {
  const { user } = useAuth0();
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const unitSystem = useAtomValue(state.unitSystem);
  const { defectLegendColors } = useDefectLegendColors();
  const { getUnitSuffix } = useUnitConversion();
  const structureId = useGetStructureId();
  const editableFieldRef = useRef<EditableFieldRef>(null);
  const structurePipesByStructureId = useAtomValue(state.structurePipesByStructureId);
  const userRoles = user?.['http://abyss.com/user/roles'] as string[] | undefined;
  const editableFields = useMemo(() => {
    const assessmentDetailsFields =
      assemblyTemplate?.assessmentDetails?.flatMap((detail) =>
        detail.options
          ?.filter(
            (option) =>
              option.isEditable && userRoles?.some((role) => option.editRoles?.includes(role))
          )
          .map((option) => option.fieldKey)
      ) ?? [];

    const fields =
      assemblyTemplate?.fields
        .filter(
          (field) => field.isEditable && userRoles?.some((role) => field.editRoles?.includes(role))
        )
        .map((field) => field.key ?? '') ?? [];
    return Array.from(new Set([...assessmentDetailsFields, ...fields]));
  }, [assemblyTemplate?.assessmentDetails, assemblyTemplate?.fields, userRoles]);

  const getFieldName = useCallback(
    (key: string) =>
      assemblyTemplate?.fields.find((field) => field.key === key)?.name ??
      titleCaseSentence(key.replaceAll(/_/g, ' ')),
    [assemblyTemplate?.fields]
  );

  const getFieldUnit = useCallback(
    ({
      unit,
      fieldValue,
      displayUnit,
    }: {
      unit: UnitsEnum;
      fieldValue: number;
      displayUnit?: UnitsEnum | undefined;
    }) => {
      return localiseMeasurement({
        unitSystem,
        sourceUnit: unit,
        value: Number(fieldValue),
        displayAsUnit: displayUnit ?? undefined,
      });
    },
    [unitSystem]
  );

  const formatValue = useCallback(
    (key: string, value: string | number) => {
      const field = assemblyTemplate?.fields.find((field) => field.key === key);

      if (field?.displayConfig?.sortType === SortTypeEnum.Number) {
        const roundedValue = field?.roundToDecimal
          ? Number(value).toFixed(field?.roundToDecimal)
          : Number(value);
        const formattedValue = field.unit
          ? `${getFieldUnit({
            unit: field.unit,
            fieldValue: Number(value),
            displayUnit: field.displayUnit ?? undefined,
          })}`
          : `${roundedValue}`;
        return !formattedValue || Number.isNaN(Number.parseFloat(formattedValue))
          ? 'N/A'
          : formattedValue;
      }

      return value ?? 'N/A';
    },
    [assemblyTemplate?.fields, getFieldUnit]
  );

  const getConditionColor = useCallback(
    (title: string) =>
      defectLegendColors[title] ??
      coatingConditionLegend[title as keyof typeof coatingConditionLegend] ??
      'black',
    [defectLegendColors]
  );

  const calculateAssemblyValues = useCallback(
    (rawValue: string | number) => {
      const parsed = Number.parseFloat(rawValue.toString());
      if (Number.isNaN(parsed)) {
        return {
          corrosionValue: 'N/A',
          assemblyRatio: '0.00%',
        };
      }
      const fixed = parsed.toFixed(3);
      const percent = calculatePercentage(Number(assemblyArea), parsed);
      return {
        corrosionValue: localiseMeasurement({
          unitSystem,
          sourceUnit: UnitsEnum.SquareMeter,
          value: Number(fixed),
        }),
        assemblyRatio: Number.isNaN(percent) ? '0.00%' : `${percent}%`,
      };
    },
    [assemblyArea, unitSystem]
  );

  const renderDefectRow = useCallback(
    (label: string, ratio: string, color: string) => (
      <Stack direction="row" alignItems="center" sx={{ width: '100%' }} key={label}>
        <Typography
          variant="body1"
          sx={{
            flexGrow: 1,
            fontWeight: 500,
            color: abyssColors.primary[500],
            fontSize: '1.2rem',
          }}
        >
          {label}
        </Typography>
        <CircularProgress
          value={Number(ratio.replace('%', '')) || 0}
          backgroundColor={abyssColors.primary[100]}
          progressColor={color}
          size={25}
        />
      </Stack>
    ),
    []
  );

  const handleSavePlannerValue = useCallback(
    (value: string, fieldKey: string, columnName?: string) => {
      editableFieldRef.current?.saveChanges(value, fieldKey, columnName);
    },
    []
  );

  const getFormattedValueDetails = useCallback(
    (key: string, value: string | number, allowEditing: boolean) => {
      if (!assemblyDetails) return;

      const templateField = assemblyTemplate?.fields.find((f) => f.key === key);
      const displayType = templateField?.displayConfig?.type;

      // Determine if this specific field is editable by the user
      const isEdtingAllowed = allowEditing && editableFields.includes(key);

      const DateInline = ({ fieldKey, fieldValue, fieldName }: InlineProps) => {
        return (
          <LazyDatePickerCell
            value={fieldValue}
            enableEditing={isEdtingAllowed}
            onSave={(newValue) => handleSavePlannerValue(newValue, fieldKey, fieldName)}
            fontSize="1.2rem"
            iconSize="1.6rem"
            ariaLabel={fieldName}
            nativePickerAnchor="right"
            nativePickerInset={{ right: 50 }}
          />
        );
      };

      const DropdownInline = ({ fieldKey, fieldValue, fieldName }: InlineProps) => {
        if (!isEdtingAllowed) return <Typography>{fieldValue || 'N/A'}</Typography>;

        return (
          <TextField
            select
            value={fieldValue}
            variant='standard'
            onChange={(e) => {
              handleSavePlannerValue(e.target.value, fieldKey, fieldName)
            }}
            slotProps={{
              input: {
                disableUnderline: true,
                sx: {
                  flex: 1,
                  fontWeight: 500,
                  color: abyssColors.primary[500],
                },
              },
            }}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiInput-underline:before, & .MuiInput-underline:after': { borderBottom: 'none' },
              '& .MuiInputBase-input': { fontWeight: 500, fontSize: '1.2rem' },
            }}
          >
            {(templateField?.options || []).map((opt) => {
              return (
                <MenuItem key={String(opt)} value={String(opt)}>
                  {String(opt)}
                </MenuItem>
              );
            })}
          </TextField>
        );
      };

      if (displayType === TableCellStyleEnum.Date) {
        return (
          <Box sx={{ minWidth: 100, width: '100%' }}>
            <DateInline fieldKey={key} fieldValue={value} fieldName={getFieldName(key)} />
          </Box>
        );
      }

      if (displayType === TableCellStyleEnum.Dropdown) {
        return <DropdownInline fieldKey={key} fieldValue={value} fieldName={getFieldName(key)} />;
      }

      const templateList = assemblyTemplate?.equipmentDetails?.filter(
        (detail) => detail.type === EquipmentDetailTypeEnum.TemplateList
      );

      const matchingRows =
        templateList?.flatMap((item) => {
          const fields = item.options.filter(
            (option) =>
              option.type === OptionFieldTypeEnum.CircularProgressBar && option.fieldKey === key
          );

          const fieldsData = getTemplateFieldData(fields, assemblyDetails);

          return fieldsData
            .filter((field) => field.name)
            .map((field) => {
              const title = field.name!;
              const { corrosionValue, assemblyRatio } = calculateAssemblyValues(value);
              const color = getConditionColor(title);
              return renderDefectRow(`${corrosionValue} - ${assemblyRatio}`, assemblyRatio, color);
            });
        }) ?? [];

      if (matchingRows.length > 0) {
        return matchingRows;
      }
      if (key === 'size') {
        value = getPipeSize({
          size: Number(assemblyDetails[key]),
          getUnitSuffix,
          unitSystem,
          displayUnit: false,
          structureSteelPipes: structurePipesByStructureId[structureId],
        }) as string;
      }

      return (
        <EditableReportField
          ref={editableFieldRef}
          allowEditing={isEdtingAllowed}
          fieldName={getFieldName(key)}
          fieldKey={key}
          value={value}
          editableFields={editableFields}
          templateId={assemblyTemplate?.id ?? ''}
          formatValue={formatValue}
          assemblyId={assemblyId ?? ''}
        />
      );
    },
    [
      assemblyDetails,
      assemblyTemplate?.fields,
      assemblyTemplate?.equipmentDetails,
      assemblyTemplate?.id,
      editableFields,
      getFieldName,
      formatValue,
      assemblyId,
      calculateAssemblyValues,
      getConditionColor,
      renderDefectRow,
      getUnitSuffix,
      unitSystem,
      structurePipesByStructureId,
      structureId,
      handleSavePlannerValue,
    ]
  );

  const displayableFields = useMemo(() => {
    const reviewPanelConfig = assemblyTemplate?.reviewPanelConfig ?? [];

    const allFieldKeys = reviewPanelConfig
      .flatMap((group) => group.groupSection.flatMap((section) => section?.fieldKeys ?? []))
      .filter((key): key is string => typeof key === 'string');

    return allFieldKeys;
  }, [assemblyTemplate?.reviewPanelConfig]);

  return {
    getFieldName,
    formatValue,
    displayableFields,
    editableFields,
    getFormattedValueDetails,
  };
};
